/**
 * Dashboard Page - Main Component
 * Refactored with Clean Architecture pattern
 * With configurable sections and width
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  DollarSign,
  ShoppingCart,
  Table2,
  Wallet,
  TrendingUp,
  Clock,
  User,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Package,
  Settings,
  Calendar,
} from 'lucide-react';

import { DashboardConfigModal, loadDashboardConfig, saveDashboardConfig } from './DashboardConfigModal';
import './styles.css';

const COLORS = ['#f97316', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

// Width class mapping
const widthClasses = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [stockAlerts, setStockAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Config state
  const [config, setConfig] = useState(() => loadDashboardConfig());
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, salesRes, alertsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-sales'),
        api.get('/dashboard/stock-alerts'),
      ]);
      setStats(statsRes.data);
      setRecentSales(salesRes.data);
      if (alertsRes.data.success) {
        setStockAlerts(alertsRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSaveConfig = (newConfig) => {
    setConfig(newConfig);
    saveDashboardConfig(newConfig);
  };

  const getSectionConfig = (sectionId) => {
    return config.find(s => s.id === sectionId) || { enabled: true, width: 2 };
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const paymentData = stats?.payment_totals
    ? Object.entries(stats.payment_totals).map(([name, value]) => ({ name, value }))
    : [];

  const hourlyData = stats?.hourly_sales
    ? Object.entries(stats.hourly_sales)
        .map(([hour, value]) => ({ hour: hour.slice(11, 16), value }))
        .sort((a, b) => a.hour.localeCompare(b.hour))
    : [];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get ordered sections
  const orderedSections = [...config].sort((a, b) => a.order - b.order);

  // Render section by ID with width support
  const renderSection = (section) => {
    const { id, width = 2 } = section;
    const widthClass = widthClasses[width] || widthClasses[2];

    switch (id) {
      case 'stats':
        return (
          <div key="stats" className={`${widthClass} grid grid-cols-2 md:grid-cols-4 gap-4`}>
            <Card className="stat-card" data-testid="stat-total-today">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.todaySales')}</CardTitle>
                <DollarSign className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{formatCurrency(stats?.total_today || 0)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.sales_count || 0} vendas realizadas
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card" data-testid="stat-open-orders">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.openOrders')}</CardTitle>
                <ShoppingCart className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.open_orders || 0}</div>
                <p className="text-xs text-muted-foreground">comandas em aberto</p>
              </CardContent>
            </Card>

            <Card className="stat-card" data-testid="stat-occupied-tables">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.occupiedTables')}</CardTitle>
                <Table2 className="h-4 w-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.occupied_tables || 0}</div>
                <p className="text-xs text-muted-foreground">mesas em uso</p>
              </CardContent>
            </Card>

            <Card className="stat-card" data-testid="stat-open-cashiers">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.openCashiers')}</CardTitle>
                <Wallet className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.open_cashiers || 0}</div>
                <p className="text-xs text-muted-foreground">caixas operando</p>
              </CardContent>
            </Card>
          </div>
        );

      case 'expiryAlerts':
        if (!stockAlerts?.expiring_soon || stockAlerts.expiring_soon.count === 0) {
          return null;
        }
        return (
          <Card key="expiryAlerts" className={`${widthClass} border-red-500/50`} data-testid="expiry-alert-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-red-500">
                <Calendar className="h-5 w-5" />
                Alertas de Validade
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {stockAlerts.expiring_soon.count} lote(s) vencendo nos próximos {stockAlerts.expiring_soon.days} dias
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-2">
                  {stockAlerts.expiring_soon.items.map((batch) => (
                    <div key={batch.id} className="flex items-center justify-between p-2 rounded-lg bg-red-500/10">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">{batch.product_name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">Lote: {batch.batch_number}</span>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <Badge variant="destructive" className="text-xs">
                          {batch.days_until_expiry === 0 ? 'Hoje' : 
                           batch.days_until_expiry < 0 ? 'Vencido' : 
                           `${batch.days_until_expiry}d`}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">Qtd: {batch.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <Button variant="outline" size="sm" className="w-full mt-3" asChild>
                <Link to="/stock/batches">
                  Gerenciar lotes <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        );

      case 'stockAlerts':
        if (!stockAlerts?.low_stock || stockAlerts.low_stock.count === 0) {
          return null;
        }
        return (
          <Card key="stockAlerts" className={`${widthClass} border-amber-500/50`} data-testid="low-stock-alert-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-500">
                <AlertTriangle className="h-5 w-5" />
                Estoque Baixo
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {stockAlerts.low_stock.count} produto(s) com estoque abaixo do mínimo
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-2">
                  {stockAlerts.low_stock.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-2 rounded-lg bg-amber-500/10">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-mono font-semibold text-amber-500">{item.stock}</span>
                        <span className="text-xs text-muted-foreground"> / {item.min_stock_quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <Button variant="outline" size="sm" className="w-full mt-3" asChild>
                <Link to="/stock/movements">
                  Registrar entrada <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        );

      case 'hourlyChart':
        return (
          <Card key="hourlyChart" className={widthClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Vendas por Hora
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {hourlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="hour" className="text-xs" />
                      <YAxis className="text-xs" tickFormatter={(v) => `R$${v}`} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Vendas']}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    {t('dashboard.noSalesToday')}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );

      case 'paymentChart':
        return (
          <Card key="paymentChart" className={widthClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                {t('dashboard.salesByPayment')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                {paymentData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {paymentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Sem dados de pagamento
                  </div>
                )}
              </div>
              {paymentData.length > 0 && (
                <div className="mt-2 space-y-1">
                  {paymentData.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <span>{item.name}</span>
                      </div>
                      <span className="font-mono font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 'topProducts':
        return (
          <Card key="topProducts" className={widthClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
                {t('dashboard.topProducts')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_products?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_products.map((product, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.quantity} vendidos</p>
                        </div>
                      </div>
                      <span className="font-mono font-semibold">{formatCurrency(product.total)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-muted-foreground">
                  {t('dashboard.noSalesToday')}
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 'recentSales':
        return (
          <Card key="recentSales" className={widthClass}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                {t('dashboard.recentSales')}
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/reports/sales">
                  Ver todas <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px]">
                {recentSales.length > 0 ? (
                  <div className="space-y-3">
                    {recentSales.slice(0, 6).map((sale) => (
                      <div
                        key={sale.id}
                        className="p-3 rounded-lg bg-muted/50 border border-border/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {sale.customer_name || 'Cliente não identificado'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatTime(sale.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-semibold text-primary">{formatCurrency(sale.total)}</p>
                            <p className="text-xs text-muted-foreground">{sale.items_count || sale.items?.length || 0} itens</p>
                          </div>
                        </div>
                        {sale.items && sale.items.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <div className="flex flex-wrap gap-1">
                              {sale.items.slice(0, 3).map((item, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs font-normal">
                                  {item.quantity}x {item.product_name?.slice(0, 15)}
                                  {item.product_name?.length > 15 ? '...' : ''}
                                </Badge>
                              ))}
                              {sale.items.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{sale.items.length - 3}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground">
                    Nenhuma venda recente
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page" data-testid="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground">Visão geral das vendas de hoje</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setConfigModalOpen(true)}
            data-testid="dashboard-config-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Configurar
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button asChild>
            <Link to="/pdv" data-testid="goto-pdv-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Abrir PDV
            </Link>
          </Button>
        </div>
      </div>

      {/* Render sections in configured order with grid */}
      <div className="dashboard-grid">
        {orderedSections
          .filter(s => s.enabled)
          .map(section => renderSection(section))}
      </div>

      {/* Config Modal */}
      <DashboardConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        config={config}
        onSave={handleSaveConfig}
      />
    </div>
  );
}
